import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, ChevronUp } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

interface StickyBookingButtonProps {
  onBookNow?: () => void;
  label?: string;
  href?: string;
}

export const StickyBookingButton = ({ 
  onBookNow, 
  label = "Book Your Experience",
  href
}: StickyBookingButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let ticking = false;

    const toggleVisibility = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          
          // Show button after scrolling down 300px
          setIsVisible(scrollY > 300);

          // Show scroll to top after scrolling down 800px
          setShowScrollTop(scrollY > 800);

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', toggleVisibility, { passive: true });
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleClick = () => {
    if (onBookNow) {
      onBookNow();
    } else if (href) {
      navigate(href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Default behavior: scroll to experiences section on homepage
      navigate('/#experiences');
      setTimeout(() => {
        const element = document.getElementById('experiences');
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // Don't show on certain pages
  const hiddenPaths: string[] = [];
  if (hiddenPaths.some(path => location.pathname.includes(path))) {
    return null;
  }

  return (
    <>
      {/* Sticky Booking Button */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'
        }`}
      >
        <Button
          size="lg"
          variant="accent"
          onClick={handleClick}
          className="shadow-bold hover:shadow-warm hover-scale group"
          aria-label={label}
        >
          <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
          <span className="hidden sm:inline">{label}</span>
          <span className="sm:hidden">Book Now</span>
        </Button>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-24 right-6 z-50 p-3 rounded-full bg-secondary border-2 border-border hover:bg-accent hover:border-accent hover:text-accent-foreground transition-all duration-300 shadow-nature hover:shadow-warm ${
          showScrollTop ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-5 h-5" />
      </button>
    </>
  );
};
